import React, { useState, useEffect, useRef } from 'react';
import { PAGE_META, usePageMeta } from '../lib/pageMeta';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  DocumentIcon,
  PhotoIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  PlusIcon,
  PencilIcon,
  EyeIcon,
  ArrowLeftIcon,
  UserGroupIcon,
  AcademicCapIcon,
  StarIcon,
  TrophyIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CurrencyRupeeIcon,
  BanknotesIcon,
  TrashIcon as TrashOutlineIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { downloadCsv, formatDateForCsv } from '../lib/csvExport';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Function to get token from localStorage or wherever you store it
const getToken = () => {
  return localStorage.getItem('admin_token');
};

// API service functions
const apiService = {
  async getDashboardStats() {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/trainer-dashboard/dashboard/stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch dashboard stats');
    return response.json();
  },

  async getTrainers(page = 1, search = '', status = 'all') {
    const token = getToken();
    const params = new URLSearchParams({ page, limit: 10 });
    if (search)              params.set('search', search);
    if (status !== 'all')   params.set('status', status);
    const response = await fetch(`${API_BASE_URL}/trainer-dashboard?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch trainers');
    return response.json();
  },

  async getTrainerProfile(trainerId) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/trainer-dashboard/${trainerId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch trainer profile');
    return response.json();
  },

  async createTrainer(trainerData) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/trainers`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(trainerData),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || errBody.message || 'Failed to create trainer');
    }
    return response.json();
  },

  async updateTrainer(trainerId, updateData) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/trainers/${trainerId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });
    if (!response.ok) throw new Error('Failed to update trainer');
    return response.json();
  },

  /* ── Salary Payments (via Expense tracker) ───────────────── */
  async getTrainerPayments(trainerId) {
    const token = getToken();
    const res = await fetch(
      `${API_BASE_URL}/expenses?category=trainer&trainerId=${trainerId}&limit=200&sortBy=date&sortOrder=desc`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    if (!res.ok) throw new Error('Failed to fetch trainer payments');
    const json = await res.json();
    const payments = json.data || [];
    const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
    return { data: payments, totalPaid };
  },

  async recordTrainerPayment(data) {
    const token = getToken();
    // Map legacy TrainerPayment fields → Expense fields
    const MODE_MAP = {
      cash: 'cash', bank_transfer: 'online_transfer',
      cheque: 'cheque', upi: 'upi', other: 'other',
    };
    const payload = {
      title:         `Trainer Salary — ${data.trainerName || 'Trainer'}`,
      category:      'trainer',
      amount:        data.amount,
      date:          data.paymentDate,
      paymentMethod: MODE_MAP[data.paymentMode] || 'cash',
      description:   [data.notes, data.referenceNo ? `Ref: ${data.referenceNo}` : ''].filter(Boolean).join(' | '),
      trainerId:     data.trainerId,
      batchIds:      data.batchIds || [],
      ...(data.paymentSlip ? { paymentSlip: data.paymentSlip } : {}),
    };
    const res = await fetch(`${API_BASE_URL}/expenses`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to record payment');
    }
    return res.json();
  },

  async deleteTrainerPayment(paymentId) {
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/expenses/${paymentId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to delete payment');
    return res.json();
  },
};

// ==================== SKELETON LOADER COMPONENTS ====================

// Add this CSS to your global styles or create a separate CSS file
const skeletonStyles = `
@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

@keyframes skeletonPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes bubbleExpand {
  0% {
    transform: scale(0.95);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.02);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes slideInFromLeft {
  0% {
    transform: translateX(-20px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

.skeleton-shimmer {
  animation: shimmer 2s infinite linear;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #e0e0e0 20%,
    #f0f0f0 40%,
    #f0f0f0 100%
  );
  background-size: 1000px 100%;
}

.skeleton-pulse {
  animation: skeletonPulse 1.5s ease-in-out infinite;
}

.skeleton-bubble {
  animation: bubbleExpand 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.skeleton-slide {
  animation: slideInFromLeft 0.5s ease-out forwards;
}

.skeleton-base {
  background: linear-gradient(
    90deg,
    #f3f4f6 0%,
    #e5e7eb 50%,
    #f3f4f6 100%
  );
  background-size: 200% 100%;
  border-radius: 4px;
}

.skeleton-gradient-indigo {
  background: linear-gradient(
    90deg,
    #eef2ff 0%,
    #e0e7ff 50%,
    #eef2ff 100%
  );
  background-size: 200% 100%;
}

.skeleton-gradient-violet {
  background: linear-gradient(
    90deg,
    #f5f3ff 0%,
    #ede9fe 50%,
    #f5f3ff 100%
  );
  background-size: 200% 100%;
}

.skeleton-gradient-emerald {
  background: linear-gradient(
    90deg,
    #ecfdf5 0%,
    #d1fae5 50%,
    #ecfdf5 100%
  );
  background-size: 200% 100%;
}

.skeleton-gradient-amber {
  background: linear-gradient(
    90deg,
    #fffbeb 0%,
    #fef3c7 50%,
    #fffbeb 100%
  );
  background-size: 200% 100%;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = skeletonStyles;
  if (!document.head.querySelector('[data-skeleton-styles]')) {
    styleElement.setAttribute('data-skeleton-styles', 'true');
    document.head.appendChild(styleElement);
  }
}

// Skeleton Component
const SkeletonBox = ({ className = '', variant = 'default', delay = 0 }) => {
  const variantClasses = {
    default: 'skeleton-base skeleton-shimmer',
    indigo: 'skeleton-gradient-indigo skeleton-shimmer',
    violet: 'skeleton-gradient-violet skeleton-shimmer',
    emerald: 'skeleton-gradient-emerald skeleton-shimmer',
    amber: 'skeleton-gradient-amber skeleton-shimmer',
  };

  return (
    <div
      className={`${variantClasses[variant]} skeleton-bubble ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    />
  );
};

// Dashboard Stats Skeleton
const DashboardStatsSkeleton = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {[
        { variant: 'indigo', delay: 0 },
        { variant: 'emerald', delay: 100 },
        { variant: 'violet', delay: 200 },
        { variant: 'amber', delay: 300 }
      ].map((stat, index) => (
        <div key={index} className="bg-white border border-gray-200 p-6 skeleton-bubble" style={{ animationDelay: `${stat.delay}ms` }}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <SkeletonBox className="h-3 w-24 mb-3" variant={stat.variant} delay={stat.delay} />
              <SkeletonBox className="h-8 w-16" variant={stat.variant} delay={stat.delay + 50} />
            </div>
            <SkeletonBox className="h-5 w-5 rounded-full" variant={stat.variant} delay={stat.delay + 100} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Table Row Skeleton
const TableRowSkeleton = ({ delay = 0 }) => {
  return (
    <TableRow className="hover:bg-gray-50 skeleton-slide" style={{ animationDelay: `${delay}ms` }}>
      {/* Trainer Column */}
      <TableCell className="min-w-60">
        <div className="flex items-center gap-3">
          <SkeletonBox className="w-10 h-10 rounded" delay={delay} />
          <div className="flex-1 space-y-2">
            <SkeletonBox className="h-4 w-32" delay={delay + 50} />
            <SkeletonBox className="h-3 w-24" delay={delay + 100} />
          </div>
        </div>
      </TableCell>
      {/* Contact Column */}
      <TableCell className="min-w-44">
        <div className="space-y-2">
          <SkeletonBox className="h-4 w-36" delay={delay + 150} />
          <SkeletonBox className="h-3 w-28" delay={delay + 200} />
        </div>
      </TableCell>
      {/* Experience Column */}
      <TableCell className="min-w-28">
        <SkeletonBox className="h-4 w-16" delay={delay + 250} />
      </TableCell>
      {/* Expertise Column */}
      <TableCell className="min-w-48">
        <div className="flex gap-1">
          <SkeletonBox className="h-6 w-16 rounded" variant="indigo" delay={delay + 300} />
          <SkeletonBox className="h-6 w-20 rounded" variant="violet" delay={delay + 350} />
        </div>
      </TableCell>
      {/* Students Column */}
      <TableCell className="min-w-24">
        <SkeletonBox className="h-4 w-8 mx-auto" delay={delay + 400} />
      </TableCell>
      {/* Batches Column */}
      <TableCell className="min-w-28">
        <SkeletonBox className="h-4 w-16" delay={delay + 450} />
      </TableCell>
      {/* Rating Column */}
      <TableCell className="min-w-24">
        <SkeletonBox className="h-4 w-12" variant="amber" delay={delay + 500} />
      </TableCell>
      {/* Status Column */}
      <TableCell className="min-w-28">
        <SkeletonBox className="h-6 w-16 rounded" variant="emerald" delay={delay + 550} />
      </TableCell>
      {/* Actions Column */}
      <TableCell className="min-w-24">
        <div className="flex items-center justify-end gap-2">
          <SkeletonBox className="h-8 w-8 rounded" delay={delay + 600} />
          <SkeletonBox className="h-8 w-8 rounded" delay={delay + 650} />
        </div>
      </TableCell>
    </TableRow>
  );
};

// Profile Header Skeleton
const ProfileHeaderSkeleton = () => {
  return (
    <div className="bg-white border border-gray-200 skeleton-bubble">
      <div className="p-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <SkeletonBox className="w-32 h-32 rounded flex-shrink-0" variant="indigo" />
          <div className="flex-1 space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <SkeletonBox className="h-8 w-48" delay={100} />
                <SkeletonBox className="h-6 w-16 rounded" variant="emerald" delay={150} />
              </div>
              <SkeletonBox className="h-4 w-64 mt-2" delay={200} />
              <SkeletonBox className="h-4 w-24 mt-2" variant="amber" delay={250} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[0, 1, 2].map((i) => (
                <div key={i}>
                  <SkeletonBox className="h-3 w-16 mb-2" delay={300 + i * 50} />
                  <SkeletonBox className="h-4 w-32" delay={350 + i * 50} />
                </div>
              ))}
            </div>

            <div>
              <SkeletonBox className="h-3 w-20 mb-3" delay={500} />
              <div className="flex flex-wrap gap-2">
                {[0, 1, 2, 3].map((i) => (
                  <SkeletonBox
                    key={i}
                    className="h-7 w-20 rounded"
                    variant={['indigo', 'violet', 'emerald', 'amber'][i]}
                    delay={550 + i * 50}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile Stats Skeleton
const ProfileStatsSkeleton = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {[
        { variant: 'indigo', delay: 0 },
        { variant: 'violet', delay: 100 },
        { variant: 'emerald', delay: 200 },
        { variant: 'amber', delay: 300 }
      ].map((stat, index) => (
        <div key={index} className="bg-white border border-gray-200 p-6 skeleton-bubble" style={{ animationDelay: `${stat.delay}ms` }}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <SkeletonBox className="h-3 w-28 mb-3" variant={stat.variant} delay={stat.delay} />
              <SkeletonBox className="h-9 w-12" variant={stat.variant} delay={stat.delay + 50} />
            </div>
            <SkeletonBox className="h-5 w-5 rounded-full" variant={stat.variant} delay={stat.delay + 100} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Batch Card Skeleton
const BatchCardSkeleton = ({ delay = 0 }) => {
  return (
    <div className="border border-gray-200 p-4 skeleton-bubble" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex flex-col lg:flex-row justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <SkeletonBox className="h-4 w-40" delay={delay} />
            <SkeletonBox className="h-6 w-16 rounded" variant="emerald" delay={delay + 50} />
          </div>
          <SkeletonBox className="h-3 w-48 mb-4" delay={delay + 100} />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i}>
                <SkeletonBox className="h-3 w-20 mb-2" delay={delay + 150 + i * 50} />
                <SkeletonBox className="h-4 w-24" delay={delay + 200 + i * 50} />
              </div>
            ))}
          </div>

          <div className="mt-4">
            <SkeletonBox className="h-3 w-24 mb-2" delay={delay + 400} />
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2].map((i) => (
                <SkeletonBox
                  key={i}
                  className="h-7 w-32 rounded"
                  variant="violet"
                  delay={delay + 450 + i * 50}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== END OF SKELETON LOADERS ====================

// Batch Card Component for consistent display
const BatchCard = ({ batch }) => {
  return (
    <div key={batch._id} className="border border-white/50 bg-white/60 backdrop-blur-md rounded-xl p-4 shadow-sm">
      <div className="flex flex-col lg:flex-row justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h4 className="text-sm text-gray-900">{batch.name}</h4>
            <Badge className={`${getBatchStatusColor(batch.status)} border text-xs`}>
              {batch.status}
            </Badge>
          </div>
          <p className="text-xs text-gray-600 mb-4">{batch.courseName} ({batch.courseCode})</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <p className="text-gray-500 uppercase tracking-wide">Mode</p>
              <p className="text-gray-900 capitalize mt-1">{batch.mode}</p>
            </div>
            <div>
              <p className="text-gray-500 uppercase tracking-wide">Students</p>
              <p className="text-gray-900 mt-1">{batch.assignedStudents} / {batch.capacity}</p>
            </div>
            <div>
              <p className="text-gray-500 uppercase tracking-wide">Start Date</p>
              <p className="text-gray-900 mt-1">{new Date(batch.startDate).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-gray-500 uppercase tracking-wide">End Date</p>
              <p className="text-gray-900 mt-1">{new Date(batch.endDate).toLocaleDateString()}</p>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Schedule</p>
            <div className="flex flex-wrap gap-2">
              {batch.schedule?.map((s, i) => (
                <span key={i} className="px-2 py-1 bg-gray-50 text-gray-700 text-xs border border-gray-200">
                  {s.day}: {s.startTime}-{s.endTime}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getStatusColor = (isActive) => {
  return isActive
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-gray-50 text-gray-600 border-gray-200';
};

const getBatchStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'active': return 'bg-emerald-100 text-emerald-700';
    case 'upcoming': return 'bg-blue-100 text-blue-800';
    case 'completed': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const TRAINER_DOCUMENT_TYPES = [
  { value: 'id', label: 'ID Card' },
  { value: 'resume', label: 'Resume/CV' },
  { value: 'degree-certificate', label: 'Degree Certificate' },
  { value: 'experience-certificate', label: 'Experience Certificate' },
  { value: 'photo', label: 'Photo' },
  { value: 'adhaar', label: 'Aadhaar Card' },
  { value: 'pancard', label: 'PAN Card' }
];


export default function TrainerManagement({ user }) {
  usePageMeta(PAGE_META.trainers);
  const isAdmin = user?.role === 'admin';
  const [trainersList, setTrainersList] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalTrainers: 0,
    activeTrainers: 0,
    avgExperience: 0,
    avgRating: 0
  });
  const [loading, setLoading] = useState(true);
  const [addingTrainer, setAddingTrainer] = useState(false);
  const [updatingTrainer, setUpdatingTrainer] = useState(false);
  const [deletingDocType, setDeletingDocType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalTrainers: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const [viewMode, setViewMode] = useState('list');
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [trainerBatches, setTrainerBatches] = useState([]);

  /* ── Salary payment state ── */
  const [trainerPayments, setTrainerPayments]         = useState([]);
  const [totalPaid, setTotalPaid]                     = useState(0);
  const [paymentDialogOpen, setPaymentDialogOpen]     = useState(false);
  const [recordingPayment, setRecordingPayment]       = useState(false);
  const [uploadingSlip, setUploadingSlip]             = useState(false);
  const slipInputRef = useRef(null);
  const [paymentForm, setPaymentForm] = useState({
    batchIds:    [],
    amount:      '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'cash',
    referenceNo: '',
    notes:       '',
    paymentSlip: '',
  });

  // const [newTrainer, setNewTrainer] = useState({
  //   name: '',
  //   email: '',
  //   phone: '',
  //   experience: '',
  //   education: '',
  //   expertise: '',
  //   street: '',
  //   city: '',
  //   state: '',
  //   pincode: '',
  //   image: '',
  //   achievements: []
  // });

  const [newTrainer, setNewTrainer] = useState({
  name: '',
  email: '',
  phone: '',
  experience: '',
  education: '',
  expertise: '',
  street: '',
  city: '',
  state: '',
  pincode: '',
  image: '',
  achievements: '',
  documents: [] // Add this
});

const [uploadingFiles, setUploadingFiles] = useState({});

  const [editTrainer, setEditTrainer] = useState(null);

  // Debounce ref so rapid search keystrokes don't fire a request per keystroke
  const searchDebounceRef = useRef(null);

  // Re-fetch on page change immediately; debounce search/filter changes
  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    const delay = searchQuery ? 400 : 0;
    searchDebounceRef.current = setTimeout(() => {
      fetchDashboardData(currentPage, searchQuery, statusFilter);
    }, delay);
    return () => clearTimeout(searchDebounceRef.current);
  }, [currentPage, searchQuery, statusFilter]);

  const fetchDashboardData = async (page = currentPage, search = searchQuery, status = statusFilter) => {
    try {
      setLoading(true);
      const [statsData, trainersData] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getTrainers(page, search, status)
      ]);

      setDashboardStats(statsData);
      setTrainersList(trainersData.trainers);
      setPagination(trainersData.pagination);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // const handleAddTrainer = async (e) => {
  //   e.preventDefault();
  //   try {
  //     const trainerData = {
  //       name: newTrainer.name,
  //       email: newTrainer.email,
  //       phone: newTrainer.phone,
  //       expertise: newTrainer.expertise.split(',').map(e => e.trim()).filter(e => e),
  //       address: {
  //         street: newTrainer.street,
  //         city: newTrainer.city,
  //         state: newTrainer.state,
  //         pincode: newTrainer.pincode
  //       },
  //       experience: newTrainer.experience,
  //       education: newTrainer.education,
  //       availability: [],
  //       image: newTrainer.image,
  //       achievements: newTrainer.achievements
  //     };

  //     await apiService.createTrainer(trainerData);

  //     setNewTrainer({
  //       name: '', email: '', phone: '', experience: '', education: '',
  //       expertise: '', street: '', city: '', state: '', pincode: '', image: '', achievements: []
  //     });
  //     setIsAddDialogOpen(false);

  //     fetchDashboardData();
  //     toast.success('Trainer added successfully!');
  //   } catch (error) {
  //     console.error('Error adding trainer:', error);
  //     toast.error('Failed to add trainer');
  //   }
  // };

  const handleAddTrainer = async (e) => {
  e.preventDefault();
  setAddingTrainer(true);
  try {
    const trainerData = {
      name: newTrainer.name,
      email: newTrainer.email,
      phone: newTrainer.phone,
      expertise: newTrainer.expertise.split(',').map(e => e.trim()).filter(e => e),
      address: {
        street: newTrainer.street,
        city: newTrainer.city,
        state: newTrainer.state,
        pincode: newTrainer.pincode
      },
      experience: newTrainer.experience,
      education: newTrainer.education,
      availability: [],
      image: newTrainer.image,
      achievements: newTrainer.achievements,
      documents: newTrainer.documents // Include documents
    };

    await apiService.createTrainer(trainerData);

    // Reset form
    setNewTrainer({
      name: '',
      email: '',
      phone: '',
      experience: '',
      education: '',
      expertise: '',
      street: '',
      city: '',
      state: '',
      pincode: '',
      image: '',
      achievements: '',
      documents: []
    });

    setIsAddDialogOpen(false);
    fetchDashboardData();
    toast.success('Trainer added successfully!');
  } catch (error) {
    console.error('Error adding trainer:', error);
    toast.error(error.message || 'Failed to add trainer');
  } finally {
    setAddingTrainer(false);
  }
};

  const handleUpdateTrainer = async (e) => {
    e.preventDefault();
    setUpdatingTrainer(true);
    try {
      const updateData = {
        name: editTrainer.name,
        email: editTrainer.email,
        phone: editTrainer.phone,
        expertise: typeof editTrainer.expertise === 'string'
          ? editTrainer.expertise.split(',').map(e => e.trim()).filter(Boolean)
          : editTrainer.expertise,
        address: editTrainer.address || {},
        experience: editTrainer.experience,
        education: editTrainer.education,
        isActive: editTrainer.isActive,
        image: editTrainer.image
      };

      await apiService.updateTrainer(editTrainer._id, updateData);

      setIsEditDialogOpen(false);

      fetchDashboardData();

      if (selectedTrainer && selectedTrainer._id === editTrainer._id) {
        const updatedProfile = await apiService.getTrainerProfile(editTrainer._id);
        setSelectedTrainer(updatedProfile);
      }

      toast.success('Trainer updated successfully!');
    } catch (error) {
      console.error('Error updating trainer:', error);
      toast.error('Failed to update trainer');
    } finally {
      setUpdatingTrainer(false);
    }
  };

  const handleViewProfile = async (trainer) => {
    try {
      setLoading(true);
      const [trainerProfile, paymentsData] = await Promise.all([
        apiService.getTrainerProfile(trainer._id),
        apiService.getTrainerPayments(trainer._id).catch(() => ({ data: [], totalPaid: 0 })),
      ]);
      setSelectedTrainer(trainerProfile);
      setTrainerBatches(trainerProfile.batches || []);
      setTrainerPayments(paymentsData.data || []);
      setTotalPaid(paymentsData.totalPaid || 0);
      setViewMode('profile');
    } catch (error) {
      console.error('Error fetching trainer profile:', error);
      toast.error('Failed to fetch trainer profile');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedTrainer(null);
    setTrainerBatches([]);
    setTrainerPayments([]);
    setTotalPaid(0);
  };

  const fetchTrainerPayments = async (trainerId) => {
    try {
      const data = await apiService.getTrainerPayments(trainerId);
      setTrainerPayments(data.data || []);
      setTotalPaid(data.totalPaid || 0);
    } catch {
      toast.error('Failed to refresh payments');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount.');
      return;
    }
    setRecordingPayment(true);
    try {
      await apiService.recordTrainerPayment({
        trainerId:   selectedTrainer._id,
        trainerName: selectedTrainer.name,
        batchIds:    paymentForm.batchIds,
        amount:      Number(paymentForm.amount),
        paymentDate: paymentForm.paymentDate,
        paymentMode: paymentForm.paymentMode,
        referenceNo: paymentForm.referenceNo,
        notes:       paymentForm.notes,
        paymentSlip: paymentForm.paymentSlip,
      });
      toast.success('Salary payment recorded successfully.');
      setPaymentDialogOpen(false);
      setPaymentForm({ batchIds: [], amount: '', paymentDate: new Date().toISOString().split('T')[0], paymentMode: 'cash', referenceNo: '', notes: '', paymentSlip: '' });
      await fetchTrainerPayments(selectedTrainer._id);
    } catch (err) {
      toast.error(err.message || 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await apiService.deleteTrainerPayment(paymentId);
      toast.success('Payment deleted.');
      await fetchTrainerPayments(selectedTrainer._id);
    } catch {
      toast.error('Failed to delete payment');
    }
  };

  const handleSlipUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingSlip(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/storage/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.url) {
        setPaymentForm(f => ({ ...f, paymentSlip: data.url }));
        toast.success('Slip uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingSlip(false);
    }
  };

  const toggleBatchSelection = (batchId) => {
    setPaymentForm(f => ({
      ...f,
      batchIds: f.batchIds.includes(batchId)
        ? f.batchIds.filter(id => id !== batchId)
        : [...f.batchIds, batchId],
    }));
  };

  // File upload handler
const handleFileUpload = async (file, documentType) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    setUploadingFiles(prev => ({ ...prev, [documentType]: true }));
    
    const response = await fetch(`${API_BASE_URL}/storage/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getToken()}`,
      },
      body: formData,
    });

    const data = await response.json();

    if (data.url) {
      const newDocument = {
        type: documentType,
        fileName: data.originalName,
        fileUrl: data.url,
        uploadedName: data.filename
      };

      setNewTrainer(prev => ({
        ...prev,
        documents: [
          ...prev.documents.filter(doc => doc.type !== documentType),
          newDocument
        ]
      }));

      toast.success(`${TRAINER_DOCUMENT_TYPES.find(doc => doc.value === documentType)?.label} uploaded successfully!`);
      return newDocument;
    }
  } catch (error) {
    console.error('Error uploading file:', error);
    toast.error(`Failed to upload ${TRAINER_DOCUMENT_TYPES.find(doc => doc.value === documentType)?.label}`);
  } finally {
    setUploadingFiles(prev => ({ ...prev, [documentType]: false }));
  }
};

// File delete handler
const handleFileDelete = async (document) => {
  if (!document.fileUrl) return;
  setDeletingDocType(document.type);
  try {
    const response = await fetch(`${API_BASE_URL}/storage/delete`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uri: document.fileUrl }),
    });

    const data = await response.json();

    if (data.message) {
      setNewTrainer(prev => ({
        ...prev,
        documents: prev.documents.filter(doc => doc.type !== document.type)
      }));
      toast.success('File deleted successfully!');
    }
  } catch (error) {
    console.error('Error deleting file:', error);
    toast.error('Failed to delete file');
  } finally {
    setDeletingDocType(null);
  }
};

// File input change handler
const handleFileChange = (event, documentType) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file size (5MB limit)
  if (file.size > 5 * 1024 * 1024) {
    toast.error('File size should be less than 5MB');
    return;
  }

  handleFileUpload(file, documentType);
  event.target.value = ''; // Reset input
};

// Get document icon helper
const getDocumentIcon = (type) => {
  switch (type) {
    case 'photo':
      return <PhotoIcon className="h-4 w-4" />;
    default:
      return <DocumentIcon className="h-4 w-4" />;
  }
};


  // Filter trainers for client-side search
  const filteredTrainers = trainersList.filter(trainer => {
    const matchesSearch = trainer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trainer.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trainer.expertise?.some(e => e.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && trainer.isActive) ||
      (statusFilter === 'inactive' && !trainer.isActive);
    return matchesSearch && matchesStatus;
  });

  const handleExportTrainers = () => {
    const headers = [
      'Name', 'Email', 'Phone',
      'Expertise', 'Experience (yrs)',
      'Total Students', 'Total Batches',
      'Rating', 'Status',
    ];
    const rows = filteredTrainers.map((t) => [
      t.name  || '-',
      t.email || '-',
      t.phone || '-',
      (t.expertise || []).join('; ') || '-',
      t.experience || '-',
      t.totalStudents || 0,
      t.totalBatches  || 0,
      t.rating ?? '-',
      t.isActive ? 'Active' : 'Inactive',
    ]);
    downloadCsv(headers, rows, `trainers-export-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${rows.length} trainer records`);
  };

  const handleExportTrainerPayments = (payments, trainerName) => {
    const headers = ['Date', 'Amount (₹)', 'Payment Mode', 'Batches Covered', 'Description', 'Recorded By'];
    const rows = payments.map((p) => [
      p.date ? formatDateForCsv(p.date) : '-',
      p.amount || 0,
      (p.paymentMethod || '').replace(/_/g, ' ') || '-',
      (p.batchIds || []).map(b => b.name || b).join('; ') || '-',
      p.description || '-',
      p.createdBy?.name || '-',
    ]);
    downloadCsv(headers, rows, `trainer-payments-${trainerName || 'all'}-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${rows.length} payment records`);
  };

  // LOADING STATE FOR LIST VIEW
  if (loading && viewMode === 'list') {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <SkeletonBox className="h-8 w-64 mb-2" variant="indigo" />
            <SkeletonBox className="h-4 w-96" />
          </div>
          <SkeletonBox className="h-10 w-32 rounded" variant="violet" delay={100} />
        </div>

        <DashboardStatsSkeleton />

        <div className="bg-white border border-gray-200">
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <SkeletonBox className="h-5 w-32" />
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <SkeletonBox className="h-10 w-full sm:w-64 rounded" delay={50} />
                <SkeletonBox className="h-10 w-full sm:w-36 rounded" delay={100} />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="text-xs text-white uppercase tracking-wide min-w-60">Trainer</TableHead>
                  <TableHead className="text-xs text-white uppercase tracking-wide min-w-44">Contact</TableHead>
                  <TableHead className="text-xs text-white uppercase tracking-wide min-w-28">Experience</TableHead>
                  <TableHead className="text-xs text-white uppercase tracking-wide min-w-48">Expertise</TableHead>
                  <TableHead className="text-xs text-white uppercase tracking-wide min-w-24">Students</TableHead>
                  <TableHead className="text-xs text-white uppercase tracking-wide min-w-28">Batches</TableHead>
                  <TableHead className="text-xs text-white uppercase tracking-wide min-w-24">Rating</TableHead>
                  <TableHead className="text-xs text-white uppercase tracking-wide min-w-28">Status</TableHead>
                  <TableHead className="text-xs text-white uppercase tracking-wide min-w-24 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <TableRowSkeleton key={i} delay={i * 100} />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  }

  // LOADING STATE FOR PROFILE VIEW
  if (viewMode === 'profile' && selectedTrainer) {
    if (loading) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <SkeletonBox className="h-10 w-40 rounded" />
            <SkeletonBox className="h-10 w-32 rounded" variant="indigo" delay={100} />
          </div>

          <ProfileHeaderSkeleton />

          <ProfileStatsSkeleton />

          <div className="bg-white border border-gray-200">
            <div className="border-b border-gray-200 px-6 py-4">
              <SkeletonBox className="h-5 w-48" variant="violet" />
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {[0, 1, 2].map((i) => (
                  <BatchCardSkeleton key={i} delay={i * 150} />
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // ACTUAL PROFILE VIEW
    const activeBatches = trainerBatches.filter(b => b.status === 'active');
    const completedBatches = trainerBatches.filter(b => b.status === 'completed');
    const upcomingBatches = trainerBatches.filter(b => b.status === 'upcoming');
    const initials = selectedTrainer.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

    return (
      <div className="space-y-5">
        {/* ── Top bar ── */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleBackToList}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Trainers
          </button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setEditTrainer(selectedTrainer); setIsEditDialogOpen(true); }}
            className="flex items-center gap-1.5 text-sm"
          >
            <PencilIcon className="h-3.5 w-3.5" />
            Edit Profile
          </Button>
        </div>

        {/* ── Hero card ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* gradient banner */}
          <div className="h-24 bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600" />
          <div className="px-6 pb-6">
            {/* Avatar — pops out of banner */}
            <div className="-mt-10 mb-3">
              {selectedTrainer.image ? (
                <img
                  src={selectedTrainer.image}
                  alt={selectedTrainer.name}
                  className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow-md"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl border-4 border-white shadow-md bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">{initials}</span>
                </div>
              )}
            </div>
            {/* Name + badge — sits fully in white area */}
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">{selectedTrainer.name}</h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${selectedTrainer.isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                {selectedTrainer.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {selectedTrainer.education && (
              <p className="text-sm text-gray-500 mt-0.5">{selectedTrainer.education}</p>
            )}
            <div className="flex items-center gap-1 mt-1">
              <StarIcon className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
              <span className="text-sm font-medium text-gray-700">{selectedTrainer.rating || 0}</span>
              <span className="text-xs text-gray-400">/ 5.0</span>
            </div>

            {/* Contact + expertise row */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {selectedTrainer.email && (
                <div className="flex items-center gap-2.5 p-3 rounded-xl bg-gray-50">
                  <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Email</p>
                    <p className="text-xs font-medium text-gray-800 truncate">{selectedTrainer.email}</p>
                  </div>
                </div>
              )}
              {selectedTrainer.phone && (
                <div className="flex items-center gap-2.5 p-3 rounded-xl bg-gray-50">
                  <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                    <svg className="h-4 w-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Phone</p>
                    <p className="text-xs font-medium text-gray-800">{selectedTrainer.phone}</p>
                  </div>
                </div>
              )}
              {selectedTrainer.experience && (
                <div className="flex items-center gap-2.5 p-3 rounded-xl bg-gray-50">
                  <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <svg className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Experience</p>
                    <p className="text-xs font-medium text-gray-800">{selectedTrainer.experience}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Expertise tags */}
            {selectedTrainer.expertise?.length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-2">Expertise</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedTrainer.expertise.map((skill, i) => (
                    <span key={i} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full border border-indigo-100">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Stat cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Students', value: selectedTrainer.totalStudents || 0, icon: UserGroupIcon, color: 'bg-blue-500', light: 'bg-blue-50 text-blue-700' },
            { label: 'Active Batches',    value: activeBatches.length,    icon: AcademicCapIcon,  color: 'bg-violet-500', light: 'bg-violet-50 text-violet-700' },
            { label: 'Completed Batches', value: completedBatches.length, icon: CheckCircleIcon,  color: 'bg-emerald-500', light: 'bg-emerald-50 text-emerald-700' },
            { label: 'Rating',            value: `${selectedTrainer.rating || 0} / 5`, icon: StarIcon, color: 'bg-amber-500', light: 'bg-amber-50 text-amber-700' },
          ].map(({ label, value, icon: Icon, color, light }) => (
            <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
              <div className={`h-11 w-11 rounded-xl ${color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-xl font-bold text-gray-900 mt-0.5">{value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Achievements ── */}
        {selectedTrainer.achievements?.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
              <TrophyIcon className="h-4 w-4 text-amber-500" />
              Achievements &amp; Certifications
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {selectedTrainer.achievements.map((a, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Main profile tabs: Batches | Availability | Salary Payments ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <Tabs defaultValue="batches" className="w-full">
            {/* Tab bar */}
            <div className="border-b border-gray-100 px-6 pt-4">
              <TabsList className={`grid w-full max-w-lg ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <TabsTrigger value="batches" className="flex items-center gap-1.5">
                  <AcademicCapIcon className="h-3.5 w-3.5" />
                  Batches
                </TabsTrigger>
                <TabsTrigger value="availability" className="flex items-center gap-1.5">
                  <ClockIcon className="h-3.5 w-3.5" />
                  Availability
                </TabsTrigger>
                {isAdmin && (
                  <TabsTrigger value="payments" className="flex items-center gap-1.5">
                    <CurrencyRupeeIcon className="h-3.5 w-3.5" />
                    Salary Payments
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            {/* ── Batches tab ───────────────────────────────────────────── */}
            <TabsContent value="batches" className="p-6">
              <Tabs defaultValue="active" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="active">Active ({activeBatches.length})</TabsTrigger>
                  <TabsTrigger value="upcoming">Upcoming ({upcomingBatches.length})</TabsTrigger>
                  <TabsTrigger value="completed">Completed ({completedBatches.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="active" className="mt-6">
                  <div className="space-y-4">
                    {activeBatches.map((batch) => <BatchCard key={batch._id} batch={batch} />)}
                    {activeBatches.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <AcademicCapIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">No active batches</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="upcoming" className="mt-6">
                  <div className="space-y-4">
                    {upcomingBatches.map((batch) => <BatchCard key={batch._id} batch={batch} />)}
                    {upcomingBatches.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <CalendarDaysIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">No upcoming batches</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="completed" className="mt-6">
                  <div className="space-y-4">
                    {completedBatches.map((batch) => <BatchCard key={batch._id} batch={batch} />)}
                    {completedBatches.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <CheckCircleIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">No completed batches</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* ── Availability tab ──────────────────────────────────────── */}
            <TabsContent value="availability" className="p-6">
              {selectedTrainer.availability && selectedTrainer.availability.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedTrainer.availability.map((avail, i) => (
                    <div key={i} className="border border-gray-200 rounded-md p-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">{avail.day}</h4>
                      <div className="space-y-1">
                        {avail.slots?.map((slot, j) => (
                          <p key={j} className="text-xs text-gray-700">
                            {slot.startTime} – {slot.endTime}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <ClockIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No availability schedule set.</p>
                </div>
              )}
            </TabsContent>

            {/* ── Salary Payments tab (admin only) ─────────────────────── */}
            {isAdmin && (
              <TabsContent value="payments" className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Salary Disbursements</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {trainerPayments.length} payment{trainerPayments.length !== 1 ? 's' : ''} · Total paid:&nbsp;
                      <span className="font-semibold text-emerald-700">₹{totalPaid.toLocaleString('en-IN')}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {trainerPayments.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportTrainerPayments(trainerPayments, selectedTrainer?.name)}
                        className="text-xs gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      >
                        ↓ Export CSV
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        setPaymentForm({ batchIds: [], amount: '', paymentDate: new Date().toISOString().split('T')[0], paymentMode: 'cash', referenceNo: '', notes: '', paymentSlip: '' });
                        if (slipInputRef.current) slipInputRef.current.value = '';
                        setPaymentDialogOpen(true);
                      }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2 text-sm"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Record Payment
                    </Button>
                  </div>
                </div>

                {trainerPayments.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <BanknotesIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No payments recorded yet.</p>
                    <p className="text-xs mt-1">Click "Record Payment" to add the first one.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-md border border-gray-200">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs text-white uppercase tracking-wide">Date</TableHead>
                          <TableHead className="text-xs text-white uppercase tracking-wide">Amount</TableHead>
                          <TableHead className="text-xs text-white uppercase tracking-wide">Payment Mode</TableHead>
                          <TableHead className="text-xs text-white uppercase tracking-wide">Batches Covered</TableHead>
                          <TableHead className="text-xs text-white uppercase tracking-wide">Description</TableHead>
                          <TableHead className="text-xs text-white uppercase tracking-wide">Recorded By</TableHead>
                          <TableHead className="text-xs text-white uppercase tracking-wide text-center">Proof</TableHead>
                          <TableHead className="text-xs text-white uppercase tracking-wide text-center">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {trainerPayments.map((p) => (
                          <TableRow key={p._id} className="hover:bg-gray-50">
                            <TableCell className="text-sm text-gray-700 whitespace-nowrap">
                              {new Date(p.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </TableCell>
                            <TableCell className="text-sm font-semibold text-emerald-700 whitespace-nowrap">
                              ₹{Number(p.amount).toLocaleString('en-IN')}
                            </TableCell>
                            <TableCell>
                              <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 capitalize border border-blue-200">
                                {(p.paymentMethod || '').replace(/_/g, ' ')}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600 max-w-[180px]">
                              {p.batchIds?.length > 0
                                ? p.batchIds.map(b => b.name || b).join(', ')
                                : <span className="text-gray-400 italic">Not specified</span>}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500 max-w-[160px] truncate">{p.description || '—'}</TableCell>
                            <TableCell className="text-sm text-gray-600">{p.createdBy?.name || '—'}</TableCell>
                            <TableCell className="text-center">
                              {p.paymentSlip ? (
                                <a
                                  href={p.paymentSlip}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="View payment proof"
                                  className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 underline"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                  </svg>
                                  View
                                </a>
                              ) : (
                                <span className="text-gray-400 text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <button
                                onClick={() => handleDeletePayment(p._id)}
                                className="text-red-400 hover:text-red-600 transition-colors"
                                title="Delete payment"
                              >
                                <TrashOutlineIcon className="h-4 w-4" />
                              </button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* ── Record Payment Dialog ────────────────────────────────────────── */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CurrencyRupeeIcon className="h-5 w-5 text-emerald-600" />
                Record Salary Payment
              </DialogTitle>
              <DialogDescription>
                Recording payment for <strong>{selectedTrainer.name}</strong>
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleRecordPayment} className="space-y-4 mt-2">
              {/* Amount + Mode */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Amount (₹) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="e.g. 15000"
                    value={paymentForm.amount}
                    onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs">Payment Mode <span className="text-red-500">*</span></Label>
                  <select
                    value={paymentForm.paymentMode}
                    onChange={e => setPaymentForm(f => ({ ...f, paymentMode: e.target.value }))}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="upi">UPI</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Date + Reference */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Payment Date</Label>
                  <Input
                    type="date"
                    value={paymentForm.paymentDate}
                    onChange={e => setPaymentForm(f => ({ ...f, paymentDate: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">Reference No.</Label>
                  <Input
                    placeholder="TXN ID / Cheque No."
                    value={paymentForm.referenceNo}
                    onChange={e => setPaymentForm(f => ({ ...f, referenceNo: e.target.value }))}
                  />
                </div>
              </div>

              {/* Batch selection */}
              {trainerBatches.length > 0 && (
                <div>
                  <Label className="text-xs mb-2 block">Batches Covered (optional — select one or more)</Label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1">
                    {trainerBatches.map(b => (
                      <button
                        type="button"
                        key={b._id}
                        onClick={() => toggleBatchSelection(b._id)}
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                          paymentForm.batchIds.includes(b._id)
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'
                        }`}
                      >
                        {b.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <Label className="text-xs">Notes (optional)</Label>
                <textarea
                  rows={2}
                  placeholder="e.g. Payment for March batches"
                  value={paymentForm.notes}
                  onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
                />
              </div>

              {/* Payment Slip / Proof (optional) */}
              <div>
                <Label className="text-xs">Payment Slip / Proof (optional)</Label>
                <input
                  ref={slipInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={e => handleSlipUpload(e.target.files[0])}
                />
                {paymentForm.paymentSlip ? (
                  <div className="mt-1 flex items-center gap-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-md">
                    <svg className="h-5 w-5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <a
                      href={paymentForm.paymentSlip}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-emerald-700 underline truncate flex-1"
                    >
                      View uploaded slip
                    </a>
                    <button
                      type="button"
                      onClick={() => { setPaymentForm(f => ({ ...f, paymentSlip: '' })); if (slipInputRef.current) slipInputRef.current.value = ''; }}
                      className="text-xs text-red-500 hover:text-red-700 flex-shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={uploadingSlip}
                    onClick={() => slipInputRef.current?.click()}
                    className="mt-1 w-full flex items-center justify-center gap-2 border border-dashed border-gray-300 rounded-md px-3 py-2.5 text-sm text-gray-500 hover:border-emerald-400 hover:text-emerald-600 transition-colors disabled:opacity-60"
                  >
                    {uploadingSlip ? (
                      <>
                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                        </svg>
                        Uploading…
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Upload slip or proof (PDF / image)
                      </>
                    )}
                  </button>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={recordingPayment || uploadingSlip}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {recordingPayment ? 'Recording…' : 'Record Payment'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ACTUAL LIST VIEW (when not loading)
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl text-gray-900">Trainer Management</h1>
          <p className="text-sm text-gray-600 mt-1">Manage trainers and their assigned batches</p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportTrainers}
            disabled={filteredTrainers.length === 0}
            className="text-xs gap-1.5 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          >
            ↓ Export CSV
          </Button>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white">
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Trainer
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-[560px] p-0 overflow-hidden rounded-2xl">
            {/* Modal header */}
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <PlusIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-white">Add New Trainer</h2>
                  <p className="text-xs text-indigo-200 mt-0.5">Fill in the details below to add a trainer</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleAddTrainer}>
              <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

                {/* Basic info */}
                <div>
                  <p className="text-[11px] font-semibold text-indigo-600 uppercase tracking-widest mb-3">Basic Information</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">Full Name <span className="text-red-500">*</span></Label>
                      <Input
                        value={newTrainer.name}
                        onChange={(e) => setNewTrainer({ ...newTrainer, name: e.target.value })}
                        placeholder="e.g. Ravi Kumar"
                        className="h-9 text-sm"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">Phone <span className="text-red-500">*</span></Label>
                      <Input
                        value={newTrainer.phone}
                        onChange={(e) => setNewTrainer({ ...newTrainer, phone: e.target.value })}
                        placeholder="+91 9876543210"
                        className="h-9 text-sm"
                        required
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="text-xs font-medium text-gray-700">
                        Email <span className="text-gray-400 font-normal">(optional)</span>
                      </Label>
                      <Input
                        type="email"
                        value={newTrainer.email}
                        onChange={(e) => setNewTrainer({ ...newTrainer, email: e.target.value })}
                        placeholder="trainer@example.com"
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-100" />

                {/* Professional info */}
                <div>
                  <p className="text-[11px] font-semibold text-violet-600 uppercase tracking-widest mb-3">Professional Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">Experience <span className="text-red-500">*</span></Label>
                      <Input
                        value={newTrainer.experience}
                        onChange={(e) => setNewTrainer({ ...newTrainer, experience: e.target.value })}
                        placeholder="e.g. 5 years"
                        className="h-9 text-sm"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">Education <span className="text-red-500">*</span></Label>
                      <Input
                        value={newTrainer.education}
                        onChange={(e) => setNewTrainer({ ...newTrainer, education: e.target.value })}
                        placeholder="e.g. M.Tech in CS"
                        className="h-9 text-sm"
                        required
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="text-xs font-medium text-gray-700">Expertise <span className="text-red-500">*</span></Label>
                      <Input
                        value={newTrainer.expertise}
                        onChange={(e) => setNewTrainer({ ...newTrainer, expertise: e.target.value })}
                        placeholder="e.g. React, Node.js, MongoDB (comma-separated)"
                        className="h-9 text-sm"
                        required
                      />
                      <p className="text-[10px] text-gray-400">Separate multiple skills with commas</p>
                    </div>
                  </div>
                </div>

              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                <Button type="button" variant="outline" size="sm" onClick={() => setIsAddDialogOpen(false)} disabled={addingTrainer} className="text-sm">
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={addingTrainer}
                  className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white text-sm px-6 gap-2">
                  {addingTrainer
                    ? <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Adding…</>
                    : <><PlusIcon className="h-3.5 w-3.5" /> Add Trainer</>
                  }
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 p-6 rounded-md">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide ">Total Trainers</p>
              <p className="text-2xl text-gray-900 mt-2">{dashboardStats.totalTrainers}</p>
            </div>
            <UserGroupIcon className="h-5 w-5 text-gray-400" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-6 rounded-md">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Active Trainers</p>
              <p className="text-2xl text-gray-900 mt-2">{dashboardStats.activeTrainers}</p>
            </div>
            <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-6 rounded-md">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Avg. Experience</p>
              <p className="text-2xl text-gray-900 mt-2">{dashboardStats.avgExperience}+ yrs</p>
            </div>
            <ChartBarIcon className="h-5 w-5 text-gray-400" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-6 rounded-md">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Avg. Rating</p>
              <p className="text-2xl text-gray-900 mt-2">{dashboardStats.avgRating} / 5</p>
            </div>
            <StarIcon className="h-5 w-5 text-amber-500 fill-amber-500" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md">
        <div className="border-b border-gray-200 px-6 py-4 ">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-sm text-black">All Trainers</h2>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 text-black/60 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <Input
                  placeholder="Search trainers..."
                  value={searchQuery}
                  onChange={(e) => { setCurrentPage(1); setSearchQuery(e.target.value); }}
                  className="pl-9 bg-white/10 border-white/20 text-black placeholder:text-black/60 w-full sm:w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setCurrentPage(1); setStatusFilter(v); }}>
                <SelectTrigger className="w-full sm:w-36 bg-white/10 border-white/20 text-black">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto ">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50 hover:bg-gray-50">
                <TableHead className="text-xs text-white uppercase tracking-wide min-w-60">Trainer</TableHead>
                <TableHead className="text-xs text-white uppercase tracking-wide min-w-44">Contact</TableHead>
                <TableHead className="text-xs text-white uppercase tracking-wide min-w-28">Experience</TableHead>
                <TableHead className="text-xs text-white uppercase tracking-wide min-w-48">Expertise</TableHead>
                <TableHead className="text-xs text-white uppercase tracking-wide min-w-24">Students</TableHead>
                <TableHead className="text-xs text-white uppercase tracking-wide min-w-28">Batches</TableHead>
                <TableHead className="text-xs text-white uppercase tracking-wide min-w-24">Rating</TableHead>
                <TableHead className="text-xs text-white uppercase tracking-wide min-w-28">Status</TableHead>
                <TableHead className="text-xs text-white uppercase tracking-wide min-w-24 text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTrainers.map((trainer) => (
                <TableRow key={trainer._id} className="hover:bg-gray-50">
                  <TableCell className="min-w-60">
                    <div className="flex items-center gap-3 min-w-0">
                      {trainer.image && (
                        <img
                          src={trainer.image}
                          alt={trainer.name}
                          className="w-10 h-10 object-cover border border-gray-200 flex-shrink-0 rounded"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate" title={trainer.name}>
                          {trainer.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate" title={trainer.education}>
                          {trainer.education}
                        </p>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="min-w-44">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 truncate" title={trainer.email}>
                        {trainer.email}
                      </p>
                      <p className="text-xs text-gray-500 truncate" title={trainer.phone}>
                        {trainer.phone}
                      </p>
                    </div>
                  </TableCell>

                  <TableCell className="min-w-28">
                    <p className="text-sm text-gray-900 whitespace-nowrap">
                      {trainer.experience}
                    </p>
                  </TableCell>

                  <TableCell className="min-w-48">
                    <div className="flex flex-wrap gap-1 min-w-0">
                      {trainer.expertise?.slice(0, 2).map((skill, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs border border-gray-200 rounded whitespace-nowrap flex-shrink-0"
                        >
                          {skill}
                        </span>
                      ))}
                      {trainer.expertise?.length > 2 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs border border-gray-200 rounded whitespace-nowrap flex-shrink-0">
                          +{trainer.expertise.length - 2}
                        </span>
                      )}
                    </div>
                  </TableCell>

                  <TableCell className="min-w-24">
                    <p className="text-sm text-gray-900 text-center">
                      {trainer.totalStudents || 0}
                    </p>
                  </TableCell>

                  <TableCell className="min-w-28">
                    <p className="text-sm text-gray-900 whitespace-nowrap">
                      {trainer.activeBatches || 0} active
                    </p>
                  </TableCell>

                  <TableCell className="min-w-24">
                    <div className="flex items-center gap-1 whitespace-nowrap">
                      <StarIcon className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0" />
                      <span className="text-sm text-gray-900">{trainer.rating || 0}</span>
                    </div>
                  </TableCell>

                  <TableCell className="min-w-28">
                    <Badge className={`${getStatusColor(trainer.isActive)} border text-xs whitespace-nowrap`}>
                      {trainer.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>

                  <TableCell className="min-w-24 text-center align-middle">
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleViewProfile(trainer)}
                        className="h-8 w-8 p-0 flex items-center justify-center"
                        title="View Profile"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditTrainer(trainer);
                          setIsEditDialogOpen(true);
                        }}
                        className="h-8 w-8 p-0 flex items-center justify-center"
                        title="Edit Trainer"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredTrainers.length === 0 && (
            <div className="text-center py-12">
              <UserGroupIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 text-sm">No trainers found</p>
              <p className="text-gray-400 text-xs mt-1">Try adjusting your search or filters</p>
            </div>
          )}

          {/* Trainer pagination — driven by server-side pagination state */}
          {pagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t mt-2">
              <p className="text-sm text-gray-500">
                Showing {(pagination.currentPage - 1) * (pagination.limit || 10) + 1}–
                {Math.min(pagination.currentPage * (pagination.limit || 10), pagination.totalTrainers)} of {pagination.totalTrainers} trainers
              </p>
              <div className="flex items-center gap-1">
                <Button
                  size="sm" variant="outline"
                  disabled={!pagination.hasPrevPage}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="h-8 px-3 text-xs"
                >
                  Previous
                </Button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - pagination.currentPage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-2 text-gray-400 text-xs">…</span>
                    ) : (
                      <Button
                        key={p} size="sm"
                        variant={p === pagination.currentPage ? 'default' : 'outline'}
                        onClick={() => setCurrentPage(p)}
                        className="h-8 w-8 p-0 text-xs"
                      >
                        {p}
                      </Button>
                    )
                  )}
                <Button
                  size="sm" variant="outline"
                  disabled={!pagination.hasNextPage}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="h-8 px-3 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[560px] p-0 overflow-hidden rounded-2xl">
          {editTrainer && (
            <>
              {/* Modal header */}
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-5">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center">
                    <PencilIcon className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-white">Edit Trainer</h2>
                    <p className="text-xs text-violet-200 mt-0.5">Update the details for <span className="font-medium">{editTrainer.name}</span></p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleUpdateTrainer}>
                <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

                  {/* Basic info */}
                  <div>
                    <p className="text-[11px] font-semibold text-violet-600 uppercase tracking-widest mb-3">Basic Information</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Full Name <span className="text-red-500">*</span></Label>
                        <Input
                          value={editTrainer.name}
                          onChange={(e) => setEditTrainer({ ...editTrainer, name: e.target.value })}
                          className="h-9 text-sm"
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Phone <span className="text-red-500">*</span></Label>
                        <Input
                          value={editTrainer.phone}
                          onChange={(e) => setEditTrainer({ ...editTrainer, phone: e.target.value })}
                          className="h-9 text-sm"
                          required
                        />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-xs font-medium text-gray-700">
                          Email <span className="text-gray-400 font-normal">(optional)</span>
                        </Label>
                        <Input
                          type="email"
                          value={editTrainer.email || ''}
                          onChange={(e) => setEditTrainer({ ...editTrainer, email: e.target.value })}
                          className="h-9 text-sm"
                          placeholder="trainer@example.com"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-100" />

                  {/* Professional */}
                  <div>
                    <p className="text-[11px] font-semibold text-indigo-600 uppercase tracking-widest mb-3">Professional Details</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Experience <span className="text-red-500">*</span></Label>
                        <Input
                          value={editTrainer.experience || ''}
                          onChange={(e) => setEditTrainer({ ...editTrainer, experience: e.target.value })}
                          placeholder="e.g. 5 years"
                          className="h-9 text-sm"
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Education <span className="text-red-500">*</span></Label>
                        <Input
                          value={editTrainer.education || ''}
                          onChange={(e) => setEditTrainer({ ...editTrainer, education: e.target.value })}
                          placeholder="e.g. M.Tech in CS"
                          className="h-9 text-sm"
                          required
                        />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-xs font-medium text-gray-700">Expertise</Label>
                        <Input
                          value={Array.isArray(editTrainer.expertise) ? editTrainer.expertise.join(', ') : (editTrainer.expertise || '')}
                          onChange={(e) => setEditTrainer({ ...editTrainer, expertise: e.target.value })}
                          placeholder="e.g. React, Node.js (comma-separated)"
                          className="h-9 text-sm"
                        />
                        <p className="text-[10px] text-gray-400">Separate multiple skills with commas</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-100" />

                  {/* Settings */}
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">Settings</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Status</Label>
                        <Select
                          value={editTrainer.isActive ? 'active' : 'inactive'}
                          onValueChange={(v) => setEditTrainer({ ...editTrainer, isActive: v === 'active' })}
                        >
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">✅ Active</SelectItem>
                            <SelectItem value="inactive">⛔ Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Profile Image URL</Label>
                        <Input
                          value={editTrainer.image || ''}
                          onChange={(e) => setEditTrainer({ ...editTrainer, image: e.target.value })}
                          placeholder="https://..."
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                  <Button type="button" variant="outline" size="sm" onClick={() => setIsEditDialogOpen(false)} disabled={updatingTrainer} className="text-sm">
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" disabled={updatingTrainer}
                    className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-sm px-6 gap-2">
                    {updatingTrainer
                      ? <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Saving…</>
                      : <><PencilIcon className="h-3.5 w-3.5" /> Save Changes</>
                    }
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
